import { ReferenceMonth } from 'bold-ui'
import { format, parse } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { isValidHora } from 'util/validation'
import { isEmpty } from 'util/validation/Util'

export const DATE_REGEX_DDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/

const dateOnly = /^\d{4}-\d{2}-\d{2}$/

export const formatDate = (value: number | string | null | undefined): string =>
  value ? dateAsDdMmYyyy(toDate(value)) : '-'

export const dateAsDdMmYyyy = (value: Date | null | undefined): string => (value ? format(value, 'dd/MM/yyyy') : '-')

export const formatDayOfWeek = (value?: number | string): string =>
  value ? format(toDate(value), 'EEEE', { locale: ptBR }) : '-'

export const formatWeekdayDayMonthYear = (value?: number | string): string =>
  value
    ? format(toDate(value), "eeee, dd 'de' MMMM 'de' yyyy", {
        locale: ptBR,
      })
    : '-'

export const formatHoursMinutes = (value?: number | string): string => (value ? format(toDate(value), 'HH:mm') : '-')

export const formatDateHourMinutes = (value?: Date): string => (value ? format(value, 'HH:mm') : '-')

export const formatDayMonthYear = (value?: number | string): string =>
  value
    ? format(toDate(value), "dd 'de' MMMM 'de' yyyy", {
        locale: ptBR,
      })
    : '-'

export const formatDayMonthYearHourMinutes = (value?: number | string): string =>
  value
    ? format(toDate(value), "dd 'de' LLLL 'de' yyyy 'às' HH:mm", {
        locale: ptBR,
      })
    : '-'

export const toDate = (value: number | string | undefined): Date | null => (value ? safeToDate(value) : null)

const safeToDate = (value: number | string): Date =>
  new Date(typeof value === 'string' && dateOnly.test(value) ? `${value}T00:00:00` : value)

export const removeTimeZone = (data) => {
  return data?.toString().replace(/ \w+-\d+ \(.*\)$/, '')
}

export const dateAsYyyyMmDd = (value: Date | number | undefined): string | undefined =>
  value ? format(value, 'yyyy-MM-dd') : undefined
export const dateAsYyyyMmDdHhMm = (value: Date): string => format(value, 'yyyy-MM-dd-HH-mm')
export const dateAsYyyyMmDdHhMmSs = (value: Date): string => format(value, 'yyyy-MM-dd-HH-mm-ss')
export const safeDateAsYyyyMmDd = (date: Date | string | number): string =>
  format(date instanceof Date ? date : safeToDate(date), 'yyyy-MM-dd')

export const convertDateAndTimeToInstant = (date: LocalDate, time: string) => {
  const noDate = isEmpty(date)
  const noTime = isEmpty(time)

  if (noDate && noTime) return null
  if (noDate || noTime || !dateOnly.test(date) || !isValidHora(time)) throw new Error('Data ou hora inválida')

  const formatted = `${date} ${time}`
  return parse(formatted, 'yyyy-MM-dd HH:mm', new Date()).getTime()
}

export const formatDateAndHoursMinutes = (value?: number | string | Date, separadorDataHora = 'às'): string => {
  const dateValue = value instanceof Date ? value : toDate(value)

  return dateValue && !isNaN(dateValue.getTime()) ? format(dateValue, `dd/MM/yyyy '${separadorDataHora}' HH:mm`) : '-'
}

export const formatDateAsMonth = (date: Date): string => (date ? format(date, 'MMMM', { locale: ptBR }) : undefined)

export function formatHoursAsDays(hours: number): string {
  if (hours < 0) {
    return '-'
  }

  if (hours === 0) {
    return '0 horas'
  }

  if (hours >= 24 && hours % 24 === 0) {
    const dias = hours / 24
    return dias === 1 ? '1 dia' : `${dias} dias`
  }

  return hours === 1 ? '1 hora' : `${hours} horas`
}

export function dateAsYyyyMmDdHhMmSsWithoutDashes(value?: Date | number): string | undefined {
  return value ? format(value, 'yyyyMMddHHmmss') : undefined
}

export function parseDateToTimestamp(value: Date | string | number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null
  }

  const timestamp = new Date(value).getTime()

  return Number.isFinite(timestamp) ? timestamp : null
}

export const formatReferenceMonthAsDdMmYyyy = (value: ReferenceMonth): string | null => {
  const { year, month } = value || {}

  return value ? format(new Date(year, month, 1), 'dd/MM/yyyy') : null
}

/**
 * Converte total em minutos positivo para o formato por extenso.
 *
 * Por exemplo, 90 minutos vira "1 hora e 30 minutos".
 *
 * @param totalMinutos a duração em minutos
 * @returns a duração por extenso
 */
export const formatDuracaoPorExtenso = (totalMinutos: number): string => {
  // TODO (RNG): Ajustar para durações negativas #34194
  if (totalMinutos < 0) {
    throw new Error('Total em minutos não pode ser negativo')
  }

  const horas = Math.floor(totalMinutos / 60)
  const minutos = totalMinutos % 60

  if (horas === 0) return 'minuto'.pluralizeAndConcatValue(minutos)
  if (minutos === 0) return 'hora'.pluralizeAndConcatValue(horas)
  return `${'hora'.pluralizeAndConcatValue(horas)} e ${'minuto'.pluralizeAndConcatValue(minutos)}`
}
